import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plane, Hotel, Bus, Car, Search, ArrowRight, Users } from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";

const TABS = [
  { key: "flights", label: "Flights", icon: Plane },
  { key: "hotels", label: "Hotels", icon: Hotel },
  { key: "buses", label: "Buses", icon: Bus },
  { key: "cabs", label: "Cabs", icon: Car },
];

export default function SearchWidget({ initialTab = "flights" }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [cities, setCities] = useState([]);
  const today = fmtDate(new Date());

  const [flt, setFlt] = useState({ from: "DEL", to: "GOI", date: today, pax: 1 });
  const [bus, setBus] = useState({ from: "DEL", to: "IXC", date: today, pax: 1 });
  const [hot, setHot] = useState({ city: "Goa", checkin: today, guests: 2 });
  const [cab, setCab] = useState({ pickup: "", drop: "", date: today, km: 20 });

  useEffect(() => {
    api.get("/travel/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const go = () => {
    if (tab === "flights") navigate(`/flights?from=${flt.from}&to=${flt.to}&date=${flt.date}&pax=${flt.pax}`);
    else if (tab === "buses") navigate(`/buses?from=${bus.from}&to=${bus.to}&date=${bus.date}&pax=${bus.pax}`);
    else if (tab === "hotels") navigate(`/hotels?city=${encodeURIComponent(hot.city)}&checkin=${hot.checkin}&guests=${hot.guests}`);
    else navigate(`/cabs?pickup=${encodeURIComponent(cab.pickup)}&drop=${encodeURIComponent(cab.drop)}&date=${cab.date}&km=${cab.km}`);
  };

  const CityInput = ({ value, onChange, testid, label }) => (
    <label className="block flex-1 min-w-[140px]">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-11 rounded-lg border border-input bg-card px-3 text-sm font-medium focus:ring-2 focus:ring-accent focus:outline-none">
        {cities.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
      </select>
    </label>
  );

  return (
    <div className="rounded-2xl bg-card/95 backdrop-blur border border-primary/10 p-3 sm:p-4 shadow-xl" data-testid="search-widget">
      <div className="flex gap-1 mb-3 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${tab === t.key ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-secondary"}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {tab === "flights" && (
          <>
            <CityInput label="From" value={flt.from} onChange={(v) => setFlt({ ...flt, from: v })} testid="flt-from" />
            <CityInput label="To" value={flt.to} onChange={(v) => setFlt({ ...flt, to: v })} testid="flt-to" />
            <DateInput label="Depart" value={flt.date} onChange={(v) => setFlt({ ...flt, date: v })} min={today} testid="flt-date" />
            <NumInput label="Travellers" value={flt.pax} onChange={(v) => setFlt({ ...flt, pax: v })} testid="flt-pax" />
          </>
        )}
        {tab === "buses" && (
          <>
            <CityInput label="From" value={bus.from} onChange={(v) => setBus({ ...bus, from: v })} testid="bus-from" />
            <CityInput label="To" value={bus.to} onChange={(v) => setBus({ ...bus, to: v })} testid="bus-to" />
            <DateInput label="Travel date" value={bus.date} onChange={(v) => setBus({ ...bus, date: v })} min={today} testid="bus-date" />
            <NumInput label="Seats" value={bus.pax} onChange={(v) => setBus({ ...bus, pax: v })} testid="bus-pax" />
          </>
        )}
        {tab === "hotels" && (
          <>
            <label className="block flex-1 min-w-[160px]">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">City</span>
              <input value={hot.city} onChange={(e) => setHot({ ...hot, city: e.target.value })} placeholder="Goa, Manali…" data-testid="hot-city"
                className="w-full h-11 rounded-lg border border-input bg-card px-3 text-sm font-medium focus:ring-2 focus:ring-accent focus:outline-none" />
            </label>
            <DateInput label="Check-in" value={hot.checkin} onChange={(v) => setHot({ ...hot, checkin: v })} min={today} testid="hot-checkin" />
            <NumInput label="Guests" value={hot.guests} onChange={(v) => setHot({ ...hot, guests: v })} testid="hot-guests" />
          </>
        )}
        {tab === "cabs" && (
          <>
            <label className="block flex-1 min-w-[140px]">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Pickup</span>
              <input value={cab.pickup} onChange={(e) => setCab({ ...cab, pickup: e.target.value })} placeholder="Airport, hotel…" data-testid="cab-pickup"
                className="w-full h-11 rounded-lg border border-input bg-card px-3 text-sm font-medium focus:ring-2 focus:ring-accent focus:outline-none" />
            </label>
            <label className="block flex-1 min-w-[140px]">
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Drop</span>
              <input value={cab.drop} onChange={(e) => setCab({ ...cab, drop: e.target.value })} placeholder="Destination" data-testid="cab-drop"
                className="w-full h-11 rounded-lg border border-input bg-card px-3 text-sm font-medium focus:ring-2 focus:ring-accent focus:outline-none" />
            </label>
            <DateInput label="Date" value={cab.date} onChange={(v) => setCab({ ...cab, date: v })} min={today} testid="cab-date" />
          </>
        )}
        <button onClick={go} data-testid="search-submit"
          className="h-11 rounded-lg bg-accent text-accent-foreground px-6 font-semibold flex items-center gap-2 hover:-translate-y-px transition-transform">
          <Search size={16} /> Search
        </button>
      </div>
    </div>
  );
}

function DateInput({ label, value, onChange, min, testid }) {
  return (
    <label className="block flex-1 min-w-[130px]">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
      <input type="date" value={value} min={min} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-11 rounded-lg border border-input bg-card px-3 text-sm font-medium focus:ring-2 focus:ring-accent focus:outline-none" />
    </label>
  );
}

function NumInput({ label, value, onChange, testid }) {
  return (
    <label className="block w-24">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
      <input type="number" min={1} max={12} value={value} onChange={(e) => onChange(Math.max(1, Number(e.target.value)))} data-testid={testid}
        className="w-full h-11 rounded-lg border border-input bg-card px-3 text-sm font-medium focus:ring-2 focus:ring-accent focus:outline-none" />
    </label>
  );
}
